import React, { useMemo, useState } from 'react';
import { HtmlTable } from '../../../../../../CommonComponents/OPL/HtmlTable/HtmlTable';
import { Text } from '../../../../../../CommonComponents/OPL/Text/Text';
import { Button } from '../../../../../../CommonComponents/OPL/Button/Button';
import { Select } from '../../../../../../CommonComponents/OPL/Select/Select/Select';
import { TaxLotDetailsDTO } from '../GainLossMobile.meta';
import { formatDate } from '../../../../../../CommonUtils/formatDate';
import { TableRowMobile } from './TableRowMobile/TableRowMobile';
import {
  formatCurrency,
  formatNumber,
  formatPercentage,
} from '../../../../../../CommonUtils/formatNumber';

interface Props {
  type: string;
  taxLots: TaxLotDetailsDTO[] | null;
  goBack(): void;
}

export const TaxLotMobile = ({ type, taxLots, goBack }: Props) => {
  const [selectedTaxLot, setSelectedTaxLot] = useState(0);

  if (!taxLots) {
    return null;
  }

  const taxLotOptions = useMemo(() => {
    return taxLots.map(({ acquiredDate, sellDate }, i) => ({
      label: `${formatDate(acquiredDate)} - ${formatDate(sellDate)}`,
      value: i,
    }));
  }, [taxLots]);

  return (
    <>
      <Button
        variant="primary"
        size="large"
        leftIcon="fas fa-arrow-left"
        isTruncated
        onClick={goBack}
        width="100%"
        style={{ color: 'white' }}
        my="8"
      >
        Tax Lot Details
      </Button>
      <Select
        value={taxLotOptions?.find(({ value }) => value === selectedTaxLot)}
        options={taxLotOptions}
        onChange={opt => {
          if (opt) {
            setSelectedTaxLot(opt.value);
          }
        }}
        label="Tax Lot Details"
        m="8"
      />
      <HtmlTable variant="embedded">
        <table style={{ tableLayout: 'fixed' }}>
          <colgroup>
            <col width="50%" />
            <col width="50%" />
          </colgroup>
          <thead>
            <tr>
              <th>
                <Text variant="tableHeader" textAlign="left">
                  Name
                </Text>
              </th>
              <th>
                <Text variant="tableHeader" textAlign="right">
                  Value
                </Text>
              </th>
            </tr>
          </thead>
          <tbody>
            <TableRowMobile
              label="Units"
              value={formatNumber(taxLots[selectedTaxLot].units ?? 0, { mantissa: 2 })}
            />
            <TableRowMobile
              label="Price"
              value={formatCurrency(taxLots[selectedTaxLot].price ?? 0, { decimalPlaces: 2 })}
            />
            {type === 'unrealized' && (
              <>
                <TableRowMobile
                  label="Market Value"
                  value={formatCurrency(taxLots[selectedTaxLot].marketValue ?? 0, {
                    decimalPlaces: 2,
                  })}
                />
                <TableRowMobile
                  label="Allocation"
                  value={formatPercentage(taxLots[selectedTaxLot].percent ?? 0 / 100, {
                    mantissa: 2,
                  })}
                />
              </>
            )}
            <TableRowMobile
              label="Cost Basis"
              value={formatCurrency(taxLots[selectedTaxLot].costBasis ?? 0, { decimalPlaces: 2 })}
            />
            <TableRowMobile
              label="Short Term Gain Loss"
              value={formatCurrency(taxLots[selectedTaxLot].shortTerm ?? 0, { decimalPlaces: 2 })}
            />
            <TableRowMobile
              label="Long Term Gain Loss"
              value={formatCurrency(taxLots[selectedTaxLot].longTerm ?? 0, { decimalPlaces: 2 })}
            />
            <TableRowMobile
              label="Gain Loss"
              value={formatCurrency(taxLots[selectedTaxLot].total ?? 0, { decimalPlaces: 2 })}
            />
            <TableRowMobile
              label="Acquired Date"
              value={formatDate(taxLots[selectedTaxLot].acquiredDate)}
            />
            <TableRowMobile label="Holding Period" value={taxLots[selectedTaxLot].holdingPeriod} />
            {type === 'realized' && (
              <TableRowMobile
                label="Proceeds"
                value={formatCurrency(taxLots[selectedTaxLot].proceeds ?? 0, { decimalPlaces: 2 })}
              />
            )}
          </tbody>
        </table>
      </HtmlTable>
    </>
  );
};
